/**
 * Home's Display options (Linear's): the By Type layout and visible row
 * properties, the Dashboard's runway range, and the calendar panel. Kept
 * apart from the Mine / Team scope, which filters rather than displays.
 */
import { SlidersHorizontal } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import type { HomeLayout } from "@/components/ui/layout-toggle";

import {
  useDashboardLayout,
  type HomeProperty,
  type HomeView,
  type RunwayDays
} from "../dashboardLayoutStore";

const PROPERTIES: { id: HomeProperty; label: string }[] = [
  { id: "context", label: "Client and Visa" },
  { id: "chips", label: "Type" },
  { id: "date", label: "Date" },
  { id: "assignee", label: "Assignee" }
];

const RANGES: { days: RunwayDays; label: string }[] = [
  { days: 30, label: "30 Days" },
  { days: 120, label: "120 Days" },
  { days: 365, label: "1 Year" }
];

type Props = {
  view: HomeView;
  variant?: "ghost" | "secondary";
  /** The calendar panel toggle; omitted where there is no panel. */
  showPanelToggle?: boolean;
};

export const HomeDisplayMenu: FC<Props> = ({
  view,
  variant = "secondary",
  showPanelToggle = false
}) => {
  const layout = useDashboardLayout((s) => s.homeLayout);
  const setLayout = useDashboardLayout((s) => s.setHomeLayout);
  const properties = useDashboardLayout((s) => s.homeProperties);
  const setProperty = useDashboardLayout((s) => s.setHomeProperty);
  const runwayDays = useDashboardLayout((s) => s.runwayDays);
  const setRunwayDays = useDashboardLayout((s) => s.setRunwayDays);
  const railCollapsed = useDashboardLayout((s) => s.railCollapsed);
  const setRailCollapsed = useDashboardLayout((s) => s.setRailCollapsed);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size="xs" data-testid="home-display">
          <SlidersHorizontal />
          Display
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {view === "byType" ? (
          <>
            <DropdownMenuLabel>Layout</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={layout}
              onValueChange={(v) => setLayout(v as HomeLayout)}
            >
              <DropdownMenuRadioItem value="vertical">
                Vertical
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="horizontal">
                Horizontal
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Properties</DropdownMenuLabel>
            {PROPERTIES.map((p) => (
              <DropdownMenuCheckboxItem
                key={p.id}
                checked={properties[p.id]}
                onCheckedChange={(on) => setProperty(p.id, on === true)}
                onSelect={(e) => e.preventDefault()}
              >
                {p.label}
              </DropdownMenuCheckboxItem>
            ))}
          </>
        ) : null}
        {view === "dashboard" ? (
          <>
            <DropdownMenuLabel>Runway</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={String(runwayDays)}
              onValueChange={(v) => setRunwayDays(Number(v) as RunwayDays)}
            >
              {RANGES.map((r) => (
                <DropdownMenuRadioItem key={r.days} value={String(r.days)}>
                  {r.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        ) : null}
        {showPanelToggle ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={!railCollapsed}
              onCheckedChange={(on) => setRailCollapsed(on !== true)}
            >
              Calendar Panel
            </DropdownMenuCheckboxItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
